<template>
  <header class="ph">
    <div class="ph-top">
      <div class="ph-id">
        <span class="ph-icon" v-if="iconName"><AppIcon :name="iconName" /></span>
        <div class="ph-text">
          <div class="ph-eyebrow" v-if="eyebrowText">{{ eyebrowText }}</div>
          <h2 class="ph-title">{{ title }}</h2>
          <p class="ph-subtitle" v-if="subtitleText || $slots.subtitle"><slot name="subtitle">{{ subtitleText }}</slot></p>
        </div>
      </div>
      <div class="ph-controls" v-if="$slots.controls">
        <slot name="controls" />
      </div>
    </div>
    <div class="ph-foot" v-if="$slots.default">
      <slot />
    </div>
  </header>
</template>

<script>
import AppIcon from "@/components/icons/AppIcon.vue";
import { pageMeta } from "@/menu";

/**
 * The top of every page: an icon tile, where the page lives in the menu, its title and one
 * line on what it is for, with the page's own controls on the right. The icon, the
 * eyebrow and (when the page passes none) the subtitle come from the menu (pageMeta),
 * so a page only passes its title; each can be overridden. The default slot is a row
 * under a hairline for filters that belong to the whole page (store chips, tabs).
 */
export default {
  name: "PageHeader",
  components: { AppIcon },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    eyebrow: { type: String, default: null },
    icon: { type: String, default: null },
  },
  computed: {
    meta() {
      return pageMeta(this.$route && this.$route.name);
    },
    eyebrowText() {
      return this.eyebrow != null ? this.eyebrow : this.meta.eyebrow;
    },
    iconName() {
      return this.icon || this.meta.icon;
    },
    subtitleText() {
      return this.subtitle || this.meta.note;
    },
  },
};
</script>

<style scoped>
.ph {
  background-color: var(--bg-primary);
  background-image: radial-gradient(130% 160% at 0% 0%, var(--brand-orange-glow, rgba(249, 115, 22, 0.15)) 0%, transparent 52%);
  border: 1px solid var(--border-primary);
  border-radius: var(--card-radius, 16px);
  box-shadow: var(--card-shadow);
  padding: 28px 32px;
}
.ph-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-5, 24px);
  flex-wrap: wrap;
}
.ph-id {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  flex: 1 1 380px;
  min-width: 0;
}
.ph-icon {
  width: 52px;
  height: 52px;
  flex: none;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, var(--brand-orange, #f97316), var(--brand-orange-soft, #fdba74));
  box-shadow: 0 10px 24px -10px var(--brand-orange, #f97316);
}
.ph-icon :deep(svg) {
  width: 26px;
  height: 26px;
}
.ph-text {
  min-width: 0;
}
.ph-eyebrow {
  font-size: var(--t-caption, 12px);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-orange, #f97316);
}
.ph-title {
  margin: 6px 0 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--text-primary);
}
.ph-subtitle {
  margin: 10px 0 0;
  max-width: 62ch;
  font-size: var(--t-body, 14px);
  line-height: 1.65;
  color: var(--text-muted);
}
.ph-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}
.ph-foot {
  margin-top: 26px;
  padding-top: var(--s-5, 24px);
  border-top: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

@media (max-width: 760px) {
  .ph { padding: 20px; }
  .ph-title { font-size: 24px; }
  .ph-controls { width: 100%; justify-content: flex-start; }
}
</style>
